import { useMemo } from 'react';
import {
  NodeStatusEvent,
  NodeEvent,
  NodeEventType,
  NodeDataType,
  ErrorInfo,
  ExecutionResultEvent,
  WorkflowEventTypes,
} from '@/types/workflow-types';
import { CustomNode } from '@/types/node-types';
import { getNodeIconColor } from '@/constants/node-styling';
import {
  isNodeEvent,
  processStreamingEvents,
} from '@/pages/workflow/component/controls/helpers/panel-event-helpers';

function getStatusLabel(event: NodeEvent): string {
  switch (event.display_type) {
    case NodeDataType.THINKING:
      return 'Thinking...';
    case NodeDataType.TOOL_CALL:
      return 'Calling tool...';
    case NodeDataType.TOOL_RESULT:
      return 'Tool result';
    case NodeDataType.AGENT_MESSAGE:
    case NodeDataType.CHAT_AGENT_MESSAGE:
      return 'Responding...';
    default:
      break;
  }
  switch (event.event_type) {
    case NodeEventType.BEGIN:
      return 'Starting...';
    case NodeEventType.COMPILED:
      return 'Compiled';
    default:
      return 'Processing...';
  }
}

export interface AlertInfo {
  nodeId: string;
  displayName: string;
  count: number;
  description: string;
}

export interface NodeSummaryStats {
  nodeId: string;
  displayName: string;
  icon: string;
  iconColor: string | null;
  useThemeColors: boolean;
  events: number;
  thoughts: number;
  toolCalls: number;
  outputs: number;
  errors: number;
  retries: number;
  statusText: string;
  isRunning: boolean;
}

export interface WorkflowSummaryStats {
  totalEvents: number;
  totalThoughts: number;
  totalToolCalls: number;
  totalOutputs: number;
  completedNodes: number;
  totalNodes: number;
  isRunning: boolean;
  modelFailures: AlertInfo[];
  retries: AlertInfo[];
  nodes: NodeSummaryStats[];
}

function extractErrorInfo(data: NodeEvent['data']): ErrorInfo | null {
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    const record = data as Record<string, unknown>;
    const info =
      (record.error_info as ErrorInfo) ??
      (record.errorInfo as ErrorInfo) ??
      null;
    if (info && typeof info === 'object' && 'user_message' in info) {
      return info as ErrorInfo;
    }
  }
  return null;
}

// The authoritative, human-readable per-node failure message lives in the
// EXECUTION_RESULT event's ``execution_summary.errors[].error_info.user_message``.
// Per-node ERROR events only carry the raw transport error (e.g. a 401
// HTTPStatusError), so we map node_id -> user_message here and prefer it.
function buildUserMessageByNode(
  nodeStatuses: Record<string, NodeStatusEvent[]>,
): Map<string, string> {
  const byNode = new Map<string, string>();
  for (const events of Object.values(nodeStatuses)) {
    for (const event of events) {
      if (event.event_type !== WorkflowEventTypes.EXECUTION_RESULT) continue;
      const errors = (event as ExecutionResultEvent).data?.execution_summary
        ?.errors;
      if (!Array.isArray(errors)) continue;
      for (const entry of errors) {
        // Older backends emit plain strings (no node attribution); skip those.
        if (typeof entry !== 'object' || entry === null) continue;
        const message = entry.error_info?.user_message || entry.error;
        if (entry.node_id && message) {
          byNode.set(entry.node_id, message);
        }
      }
    }
  }
  return byNode;
}

// A retry is "resolved" once the node reaches a terminal event after the last
// RETRY_SCHEDULED. The backend's retry ``user_message`` is phrased in the
// present tense ("Retrying after a transient failure..."), which reads as if a
// retry is still in flight when shown in a finished run's summary.
function isRetryResolved(events: NodeStatusEvent[]): boolean {
  let lastRetryIndex = -1;
  let lastTerminalIndex = -1;
  events.forEach((event, i) => {
    if (!isNodeEvent(event)) return;
    if (event.event_type === NodeEventType.RETRY_SCHEDULED) {
      lastRetryIndex = i;
    } else if (
      event.event_type === NodeEventType.EXECUTION_SUCCESSFUL ||
      event.event_type === NodeEventType.EXECUTION_FAILED ||
      event.event_type === NodeEventType.END
    ) {
      lastTerminalIndex = i;
    }
  });
  return lastTerminalIndex > lastRetryIndex;
}

function computeNodeStats(
  nodeId: string,
  events: NodeStatusEvent[],
  node: CustomNode | undefined,
  workflowEnded: boolean,
  workflowPaused: boolean,
): NodeSummaryStats {
  const thoughtBlockIds = new Set<string>();
  const toolCallBlockIds = new Set<string>();
  let hasOutput = false;
  let errors = 0;
  let retries = 0;
  let statusText = 'Pending';
  let endedSuccessfully = false;
  let sawSuccess = false;
  let sawFailure = false;

  for (const event of events) {
    if (!isNodeEvent(event)) continue;

    if (event.display_type === NodeDataType.THINKING) {
      const key = event.block_id || event.event_id;
      thoughtBlockIds.add(key);
    }

    if (event.display_type === NodeDataType.TOOL_CALL) {
      const key = event.block_id || event.event_id;
      toolCallBlockIds.add(key);
    }

    // A node produces a single logical result; count it once regardless of
    // how many NODE_RESULT events arrive for it (the live stream can deliver
    // duplicates, and past runs synthesize their own terminal event).
    if (event.display_type === NodeDataType.NODE_RESULT) {
      hasOutput = true;
    }

    if (event.event_type === NodeEventType.EXECUTION_SUCCESSFUL) {
      endedSuccessfully = true;
      sawSuccess = true;
    }
    if (event.event_type === NodeEventType.EXECUTION_FAILED) {
      endedSuccessfully = false;
      sawFailure = true;
    }

    if (
      event.event_type === NodeEventType.EXECUTION_FAILED ||
      event.display_type === NodeDataType.ERROR
    ) {
      errors++;
    }

    if (event.event_type === NodeEventType.RETRY_SCHEDULED) {
      retries++;
    }
  }

  if (endedSuccessfully) {
    errors = 0;
    retries = 0;
  }

  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i];
    if (!isNodeEvent(ev)) continue;

    switch (ev.event_type) {
      case NodeEventType.EXECUTION_SUCCESSFUL:
        statusText = 'Complete';
        break;
      case NodeEventType.EXECUTION_FAILED:
        statusText = 'Failed';
        break;
      case NodeEventType.END:
        // Prefer the latest terminal outcome: a node that failed an attempt,
        // retried, then succeeded must read "Complete", not "Failed".
        // ``endedSuccessfully`` tracks the last SUCCESSFUL/FAILED in order, so
        // gate on whether any terminal outcome was seen at all before using it.
        if (sawSuccess || sawFailure) {
          statusText = endedSuccessfully ? 'Complete' : 'Failed';
        } else {
          statusText = 'Ended';
        }
        break;
      case NodeEventType.RETRY_SCHEDULED:
        statusText = 'Retrying...';
        break;
      case NodeEventType.PROCESSING:
      case NodeEventType.BEGIN:
      case NodeEventType.COMPILED:
        statusText = ev.dynamic_message || getStatusLabel(ev);
        break;
      default:
        continue;
    }
    break;
  }

  const struct = node?.data?.struct;
  const iconColor = struct
    ? getNodeIconColor(struct.node_type, struct.icon_color)
    : null;

  // Match the count shown in the node's events timeline, which consolidates
  // streaming chunks into single events before rendering.
  const nodeEvents = events.filter(
    (e) => isNodeEvent(e) && e.node_id === nodeId,
  );
  const sortedNodeEvents = [...nodeEvents].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );
  const eventsCount = processStreamingEvents(sortedNodeEvents).length;

  let isRunning =
    statusText !== 'Pending' &&
    statusText !== 'Complete' &&
    statusText !== 'Ended' &&
    statusText !== 'Failed';

  // Once the run has ended (user stopped it, or it finished/failed), a node
  // whose last event is still non-terminal will never progress — its stream
  // was cut off. Surface it as "Stopped" so it no longer renders the running
  // ring, animated dots, and progress label. On a normal completion every
  // executed node already has a terminal status, so this is a no-op there.
  if (workflowEnded && isRunning) {
    statusText = 'Stopped';
    isRunning = false;
  }

  if (workflowPaused && isRunning) {
    statusText = 'Paused';
    isRunning = false;
  }

  return {
    nodeId,
    displayName: struct?.user_config_name || struct?.display_name || nodeId,
    icon: struct?.icon || '',
    iconColor,
    useThemeColors: iconColor === null,
    events: eventsCount,
    thoughts: thoughtBlockIds.size,
    toolCalls: toolCallBlockIds.size,
    outputs: hasOutput ? 1 : 0,
    errors,
    retries,
    statusText,
    isRunning,
  };
}

export function useEventsSummary(
  nodeStatuses: Record<string, NodeStatusEvent[]>,
  nodes: CustomNode[],
  isPaused = false,
): WorkflowSummaryStats {
  return useMemo(() => {
    const nodeMap = new Map(nodes.map((n) => [n.id, n]));
    const nodeEntries = Object.entries(nodeStatuses);
    const userMessageByNode = buildUserMessageByNode(nodeStatuses);

    // A run is over once the backend emits its end-of-run signal. On a user
    // stop the backend reliably sends EXECUTION_RESULT (success=false) but not
    // always a TERMINATION, so treat either as the terminal signal. Only
    // EXECUTION_RESULT events are keyed under 'execution_result', so a presence
    // check suffices; 'workflow_events' is a mixed stream, so TERMINATION must
    // be matched by event_type.
    const workflowEvents = nodeStatuses['workflow_events'] ?? [];
    const executionResultEvents = nodeStatuses['execution_result'] ?? [];
    const hasEnded =
      executionResultEvents.length > 0 ||
      workflowEvents.some(
        (e) => e.event_type === WorkflowEventTypes.TERMINATION,
      );

    let totalEvents = 0;
    let totalThoughts = 0;
    let totalToolCalls = 0;
    let totalOutputs = 0;
    const modelFailures: AlertInfo[] = [];
    const retriesList: AlertInfo[] = [];
    const nodeStats: NodeSummaryStats[] = [];

    for (const [nodeId, events] of nodeEntries) {
      const node = nodeMap.get(nodeId);
      if (!node) continue;

      const stats = computeNodeStats(nodeId, events, node, hasEnded, isPaused);
      nodeStats.push(stats);

      totalEvents += stats.events;
      totalThoughts += stats.thoughts;
      totalToolCalls += stats.toolCalls;
      totalOutputs += stats.outputs;

      if (stats.errors > 0) {
        let description = userMessageByNode.get(nodeId) ?? '';
        for (const event of events) {
          if (description) break;
          if (!isNodeEvent(event)) continue;
          if (
            event.event_type === NodeEventType.EXECUTION_FAILED ||
            event.display_type === NodeDataType.ERROR
          ) {
            const errorInfo = extractErrorInfo(event.data);
            if (errorInfo?.user_message) {
              description = errorInfo.user_message;
              break;
            }
            if (
              typeof event.data === 'object' &&
              event.data !== null &&
              !Array.isArray(event.data)
            ) {
              const msg = (event.data as Record<string, unknown>).error;
              if (typeof msg === 'string' && msg) {
                description = msg;
                break;
              }
            }
            if (typeof event.data === 'string' && event.data) {
              description =
                event.data.length > 200
                  ? event.data.slice(0, 200) + '...'
                  : event.data;
              break;
            }
            if (event.dynamic_message) {
              description = event.dynamic_message;
              break;
            }
          }
        }
        modelFailures.push({
          nodeId,
          displayName: stats.displayName,
          count: stats.errors,
          description,
        });
      }

      if (stats.retries > 0) {
        let description = '';
        if (isRetryResolved(events)) {
          description = 'Retried after a transient failure';
        } else {
          for (const event of events) {
            if (!isNodeEvent(event)) continue;
            if (event.event_type === NodeEventType.RETRY_SCHEDULED) {
              const data = event.data as Record<string, unknown>;
              const userMessage = data?.user_message as string;
              if (userMessage) {
                description = userMessage;
                break;
              }
            }
          }
        }
        retriesList.push({
          nodeId,
          displayName: stats.displayName,
          count: stats.retries,
          description,
        });
      }
    }

    const completedNodes = nodeStats.filter(
      (n) =>
        n.statusText === 'Complete' ||
        n.statusText === 'Ended' ||
        n.statusText === 'Failed',
    ).length;

    const workflowNodes = nodes.filter(
      (n) =>
        n.type !== 'SelectNode' &&
        n.type !== 'NoteNode' &&
        n.type !== 'ArtifactNode',
    );

    const hasStarted = nodeStats.length > 0;
    const allNodesTerminal =
      hasStarted &&
      nodeStats.length > 0 &&
      nodeStats.every(
        (n) =>
          n.statusText === 'Complete' ||
          n.statusText === 'Ended' ||
          n.statusText === 'Failed',
      );
    const isRunning = hasStarted && !hasEnded && !allNodesTerminal;

    return {
      totalEvents,
      totalThoughts,
      totalToolCalls,
      totalOutputs,
      completedNodes,
      totalNodes: workflowNodes.length,
      isRunning,
      modelFailures,
      retries: retriesList,
      nodes: nodeStats,
    };
  }, [nodeStatuses, nodes, isPaused]);
}
